import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useTheme } from '../contexts/ThemeContext'
import { getVideosWithCounts, debounce } from '../lib/queryOptimizer'

type ContentType = 'movie' | 'series' | 'short_drama' | 'podcast'
type SortType = 'newest' | 'popular' | 'trending' | 'most_viewed'

interface Video {
  id: string; title: string; description: string; video_url: string
  thumbnail_url: string; content_type: ContentType; genre_id: string
  duration: number; release_year: number; is_public: boolean
  created_by: string; created_at: string; views: number
  likes_count: number; comments_count: number
  genre?: { name_en: string }; user?: { username: string }
}
interface User { id: string; username: string; role: string }
interface HomeScreenProps { currentUser: User | null }

const fmtDuration = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}
const fmtCount = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
  : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : `${n}`

const TYPE_META: Record<ContentType, { label: string; icon: string; color: string; glow: string }> = {
  movie:       { label: 'Movies',      icon: '🎬', color: '#c084fc', glow: 'rgba(192,132,252,0.3)' },
  series:      { label: 'Series',      icon: '📺', color: '#60a5fa', glow: 'rgba(96,165,250,0.3)'  },
  short_drama: { label: 'Short Drama', icon: '🎭', color: '#fb923c', glow: 'rgba(251,146,60,0.3)'  },
  podcast:     { label: 'Podcasts',    icon: '🎙️', color: '#34d399', glow: 'rgba(52,211,153,0.3)'  },
}

const SORT_OPTIONS = [
  { value: 'newest',      label: 'Newest',      icon: '✦' },
  { value: 'popular',     label: 'Most Liked',  icon: '♥' },
  { value: 'trending',    label: 'Trending',    icon: '↑' },
  { value: 'most_viewed', label: 'Most Viewed', icon: '◉' },
]

// ── Memoized Video Card ──────────────────────────────────────────────────────
const VideoCard = React.memo<{
  video: Video; isDark: boolean; isHovered: boolean
  onHover: (id: string | null) => void; index: number
}>(({ video, isDark, isHovered, onHover, index }) => {
  const meta = TYPE_META[video.content_type]
  return (
    <Link
      to={`/video/${video.id}`}
      className="vid-card"
      onMouseEnter={() => onHover(video.id)}
      onMouseLeave={() => onHover(null)}
      style={{
        display: 'block', textDecoration: 'none',
        background: isDark ? 'rgba(255,255,255,.025)' : '#fff',
        borderRadius: 18, overflow: 'hidden',
        border: `1px solid ${isHovered ? meta.color + '44' : (isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.07)')}`,
        boxShadow: isDark ? '0 4px 20px rgba(0,0,0,.4)' : '0 2px 20px rgba(0,0,0,.06)',
        animation: `fadeUp .4s ease ${(index % 12) * 30}ms both`,
      }}
    >
      <div style={{ position: 'relative', paddingTop: '56.25%', overflow: 'hidden', background: '#000' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${meta.color}, transparent)`, zIndex: 4 }} />
        <img
          src={video.thumbnail_url || 'https://via.placeholder.com/600x340/111/222?text=VidyShort'}
          alt={video.title} className="vid-thumb" loading="lazy"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .5s ease' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,.85) 0%, rgba(0,0,0,.2) 40%, transparent 70%)' }} />
        <div className="play-ring" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transform: 'scale(.8)', transition: 'all .3s ease', zIndex: 3 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, #e53935, #8e24aa)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 40px rgba(229,57,53,.6)' }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 4 }}>
          <span style={{ padding: '3px 10px', borderRadius: 8, background: meta.color + '25', border: `1px solid ${meta.color}55`, color: meta.color, fontSize: 9, fontWeight: 800, letterSpacing: '.8px' }}>
            {meta.icon} {video.content_type.replace('_', ' ').toUpperCase()}
          </span>
        </div>
        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 4 }}>
          <span style={{ padding: '3px 9px', borderRadius: 8, background: 'rgba(0,0,0,.7)', color: '#fff', fontSize: 11, fontWeight: 700 }}>
            {fmtDuration(video.duration)}
          </span>
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 14px', zIndex: 3 }}>
          <h3 className="card-title" style={{ color: '#fff', fontSize: 14, fontWeight: 700, margin: 0, lineHeight: 1.3, transition: 'color .2s', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textShadow: '0 1px 8px rgba(0,0,0,.8)' }}>
            {video.title}
          </h3>
        </div>
      </div>
      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 30, height: 30, borderRadius: 15, flexShrink: 0, background: `linear-gradient(135deg, ${meta.color}, ${meta.color}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff' }}>
          {video.user?.username?.charAt(0).toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: isDark ? '#bbb' : '#334155', fontSize: 12, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {video.user?.username || 'Unknown'}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
            <span style={{ color: isDark ? '#555' : '#94a3b8', fontSize: 11 }}>👁 {fmtCount(video.views)}</span>
            <span style={{ color: isDark ? '#555' : '#94a3b8', fontSize: 11 }}>❤️ {fmtCount(video.likes_count)}</span>
            <span style={{ color: isDark ? '#555' : '#94a3b8', fontSize: 11 }}>💬 {fmtCount(video.comments_count)}</span>
          </div>
        </div>
      </div>
    </Link>
  )
})

// ── Main Component ───────────────────────────────────────────────────────────
export default function HomeScreen({ currentUser }: HomeScreenProps) {
  const [videos, setVideos]               = useState<Video[]>([])
  const [loading, setLoading]             = useState(true)
  const [selectedType, setSelectedType]   = useState<ContentType | null>(null)
  const [sortBy, setSortBy]               = useState<SortType>('newest')
  const [searchQuery, setSearchQuery]     = useState('')
  const [showSort, setShowSort]           = useState(false)
  const [hoveredCard, setHoveredCard]     = useState<string | null>(null)
  const [featuredIndex, setFeaturedIndex] = useState(0)
  const [page, setPage]                   = useState(1)
  const VIDEOS_PER_PAGE = 24
  const navigate   = useNavigate()
  const { isDark } = useTheme()
  const isAnon     = !currentUser

  const fetchVideos = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getVideosWithCounts({ isPublic: true, contentType: selectedType, sortBy, limit: VIDEOS_PER_PAGE })
      setVideos(data)
    } catch (e) { console.error('Fetch videos error:', e) }
    finally { setLoading(false) }
  }, [selectedType, sortBy])

  const debouncedSearch = useMemo(() => debounce((q: string) => setSearchQuery(q), 300), [])

  useEffect(() => {
    if (currentUser?.role === 'admin') { navigate('/admin'); return }
    fetchVideos()
  }, [fetchVideos, currentUser])

  useEffect(() => {
    const ch = supabase.channel('home-videos-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'videos' }, () => fetchVideos())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [fetchVideos])

  useEffect(() => {
    if (videos.length < 2) return
    const t = setInterval(() => setFeaturedIndex(i => (i + 1) % Math.min(videos.length, 5)), 6000)
    return () => clearInterval(t)
  }, [videos.length])

  const filtered = useMemo(() => {
    if (!searchQuery) return videos
    const q = searchQuery.toLowerCase()
    return videos.filter(v =>
      v.title.toLowerCase().includes(q) || v.description?.toLowerCase().includes(q)
    )
  }, [videos, searchQuery])

  const paginatedVideos = useMemo(() => filtered.slice(0, page * VIDEOS_PER_PAGE), [filtered, page])

  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) observerRef.current.disconnect()
    if (!node) return
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && paginatedVideos.length < filtered.length) setPage(p => p + 1)
    }, { threshold: 0.1 })
    observerRef.current.observe(node)
  }, [paginatedVideos.length, filtered.length])

  const featured      = videos.slice(0, 5)
  const featuredVideo = featured[featuredIndex]

  if (loading && videos.length === 0) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDark ? '#080810' : '#f0f2f8' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, border: '2px solid #e53935', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .8s linear infinite', margin: '0 auto 20px' }} />
        <p style={{ color: '#555', fontSize: 13, animation: 'pulse 2s ease infinite' }}>Loading VidyShort…</p>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: isDark ? '#080810' : '#f0f2f8', fontFamily: "'DM Sans', sans-serif", overflowX: 'hidden', width: '100%' }}>
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg) } }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }
        @keyframes heroIn  { from { opacity:0; transform:scale(1.04) }      to { opacity:1; transform:scale(1) } }
        @keyframes slideIn { from { opacity:0; transform:translateX(-12px) } to { opacity:1; transform:translateX(0) } }

        .vid-card { transition: transform .35s cubic-bezier(.34,1.56,.64,1), box-shadow .35s ease; will-change: transform; }
        .vid-card:hover { transform: translateY(-6px) scale(1.01) !important; box-shadow: 0 24px 60px rgba(0,0,0,.5) !important }
        .vid-card:hover .vid-thumb  { transform: scale(1.07) !important }
        .vid-card:hover .play-ring  { opacity: 1 !important; transform: scale(1) !important }
        .vid-card:hover .card-title { color: #e53935 !important }
        .filter-pill { transition: all .2s ease }
        .filter-pill:hover { transform: translateY(-1px) }
        .sort-item:hover { background: rgba(229,57,53,.08) !important; color: #e53935 !important }
        .anon-bar:hover {
          background: linear-gradient(135deg, rgba(229,57,53,.15), rgba(142,36,170,.09)) !important;
          border-color: rgba(229,57,53,.45) !important;
        }
        ::-webkit-scrollbar       { width: 4px; height: 4px }
        ::-webkit-scrollbar-thumb { background: #2a2a2a; border-radius: 4px }
        ::selection               { background: rgba(229,57,53,.3) }

        @media (max-width: 1100px) { .anon-perks     { display: none !important; } }
        @media (max-width:  768px) { .anon-signin-btn { display: none !important; }
          .hero-section     { height: clamp(260px, 48vh, 380px) !important; }
          .hero-thumbnails  { display: none !important; }
          .hero-actions     { flex-direction: column !important; gap: 8px !important; }
          .video-grid       { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)) !important; gap: 12px !important; }
        }
        @media (max-width:  480px) {
          .hero-section { height: clamp(220px, 42vh, 280px) !important; }
          .video-grid   { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important; gap: 8px !important; }
          .filter-pill  { padding: 6px 10px !important; font-size: 11px !important; }
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════
          SUB-HEADER BLOCK  (not sticky — just normal flow)
          Renders immediately below the sticky Header.
          Order: Filter bar → Anon banner
          Both are always 100% visible. No overlap possible.
      ═══════════════════════════════════════════════════════ */}
      <div style={{
        background: isDark ? 'rgba(8,8,16,.97)' : 'rgba(240,242,248,.97)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.07)'}`,
        boxShadow: isDark ? '0 4px 20px rgba(0,0,0,.3)' : '0 4px 12px rgba(0,0,0,.06)',
      }}>

        {/* ── Filter row ── */}
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(10px, 2vw, 14px) clamp(12px, 2vw, 48px)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>

          {/* Search */}
          <div style={{ position: 'relative', flexShrink: 0, width: 'clamp(180px, 28vw, 250px)' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#555', fontSize: 13 }}>⌕</span>
            <input
              onChange={e => debouncedSearch(e.target.value)}
              placeholder="Search titles, genres…"
              style={{ width: '100%', padding: '9px 14px 9px 38px', background: isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.06)', border: `1px solid ${isDark ? 'rgba(255,255,255,.09)' : 'rgba(0,0,0,.09)'}`, borderRadius: 24, color: isDark ? '#fff' : '#0f172a', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Category pills */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1, overflowX: 'auto', scrollbarWidth: 'none' }}>
            <button className="filter-pill" onClick={() => setSelectedType(null)} style={{ padding: '7px 16px', borderRadius: 24, border: 'none', cursor: 'pointer', background: !selectedType ? 'linear-gradient(135deg,#e53935,#8e24aa)' : (isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.06)'), color: !selectedType ? '#fff' : (isDark ? '#888' : '#64748b'), fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>◈ All</button>
            {(Object.entries(TYPE_META) as [ContentType, typeof TYPE_META[ContentType]][]).map(([type, meta]) => (
              <button key={type} className="filter-pill" onClick={() => setSelectedType(selectedType === type ? null : type)} style={{ padding: '7px 14px', borderRadius: 24, border: 'none', cursor: 'pointer', background: selectedType === type ? meta.color + '22' : (isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.06)'), color: selectedType === type ? meta.color : (isDark ? '#888' : '#64748b'), fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', outline: selectedType === type ? `1px solid ${meta.color}55` : 'none' }}>
                {meta.icon} {meta.label}
              </button>
            ))}
          </div>

          {/* Count + Sort */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{ color: isDark ? '#444' : '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>
              <b style={{ color: isDark ? '#fff' : '#0f172a' }}>{filtered.length}</b> videos
            </span>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowSort(s => !s)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 24, border: `1px solid ${isDark ? 'rgba(255,255,255,.09)' : 'rgba(0,0,0,.09)'}`, background: isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.06)', color: isDark ? '#ccc' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {SORT_OPTIONS.find(o => o.value === sortBy)?.icon} {SORT_OPTIONS.find(o => o.value === sortBy)?.label} ▾
              </button>
              {showSort && (
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: isDark ? '#111318' : '#fff', border: `1px solid ${isDark ? '#2a2a2a' : '#e2e8f0'}`, borderRadius: 16, padding: 6, minWidth: 180, boxShadow: '0 20px 60px rgba(0,0,0,.4)', zIndex: 60 }}>
                  {SORT_OPTIONS.map(o => (
                    <button key={o.value} className="sort-item" onClick={() => { setSortBy(o.value as SortType); setShowSort(false) }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', background: sortBy === o.value ? 'rgba(229,57,53,.1)' : 'transparent', color: sortBy === o.value ? '#e53935' : (isDark ? '#ccc' : '#475569'), fontSize: 13, fontWeight: sortBy === o.value ? 700 : 500, textAlign: 'left', transition: 'all .15s' }}>
                      <span style={{ fontSize: 16, width: 20 }}>{o.icon}</span>{o.label}
                      {sortBy === o.value && <span style={{ marginLeft: 'auto' }}>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Anon banner — same block, right below filter row ── */}
        {isAnon && (
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(12px, 3vw, 48px) clamp(10px, 2vw, 14px)' }}>
            <div
              className="anon-bar"
              onClick={() => navigate('/login')}
              style={{
                display: 'flex', alignItems: 'center',
                gap: 'clamp(10px, 2vw, 18px)',
                cursor: 'pointer',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(229,57,53,.10), rgba(142,36,170,.06))'
                  : 'linear-gradient(135deg, rgba(229,57,53,.07), rgba(142,36,170,.04))',
                border: '1.5px solid rgba(229,57,53,.28)',
                borderRadius: 14,
                padding: 'clamp(12px, 2vw, 16px) clamp(16px, 2.5vw, 22px)',
                transition: 'all .2s ease',
              }}
            >
              {/* Avatar */}
              <div style={{ width: 44, height: 44, minWidth: 44, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(229,57,53,.22), rgba(142,36,170,.18))', border: '2px solid rgba(229,57,53,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                👤
              </div>

              {/* Text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: isDark ? '#f1f5f9' : '#1e293b', fontSize: 'clamp(13px, 2vw, 15px)', fontWeight: 700, margin: 0, lineHeight: 1.4 }}>
                  Browsing as <span style={{ color: '#e53935', fontWeight: 800 }}>Anonymous</span>
                </p>
                <p style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 'clamp(11px, 1.6vw, 13px)', fontWeight: 500, margin: '4px 0 0', lineHeight: 1.5 }}>
                  Sign in to like, comment & track your watch history
                </p>
              </div>

              {/* Perk chips — hidden ≤ 1100px */}
              <div className="anon-perks" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {[{ icon: '❤️', label: 'Like' }, { icon: '💬', label: 'Comment' }, { icon: '📜', label: 'History' }].map(p => (
                  <span key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 13px', borderRadius: 20, background: isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.05)', border: `1px solid ${isDark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.09)'}`, color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {p.icon} {p.label}
                  </span>
                ))}
              </div>

              {/* CTA — hidden ≤ 768px */}
              <button
                className="anon-signin-btn"
                onClick={e => { e.stopPropagation(); navigate('/login') }}
                style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '11px 24px', borderRadius: 28, border: 'none', background: 'linear-gradient(135deg, #e53935, #8e24aa)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '.2px', boxShadow: '0 4px 20px rgba(229,57,53,.4)', transition: 'all .2s ease' }}
              >
                Sign In
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════
          HERO SPOTLIGHT
      ═══════════════════════════════════════════════════════ */}
      {featuredVideo && !searchQuery && !selectedType && (
        <div className="hero-section" style={{ position: 'relative', height: 'clamp(260px, 44vh, 480px)', overflow: 'hidden', background: '#000' }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${featuredVideo.thumbnail_url || ''})`, backgroundSize: 'cover', backgroundPosition: 'center', animation: 'heroIn .8s ease', filter: 'brightness(0.4) saturate(1.2)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(8,8,16,.97) 0%, rgba(8,8,16,.6) 50%, rgba(8,8,16,.2) 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(8,8,16,1) 0%, transparent 60%)' }} />

          <div style={{ position: 'relative', zIndex: 5, maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px, 4vw, 48px)', height: '100%', display: 'flex', alignItems: 'center' }}>
            <div style={{ maxWidth: 560, animation: 'slideIn .6s ease', width: '100%' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#e53935', boxShadow: '0 0 8px #e53935' }} />
                <span style={{ color: TYPE_META[featuredVideo.content_type]?.color || '#e53935', fontSize: 11, fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase' }}>
                  {TYPE_META[featuredVideo.content_type]?.icon} {featuredVideo.content_type.replace('_', ' ')}
                </span>
              </div>
              <h2 style={{ color: '#fff', fontSize: 'clamp(22px, 4vw, 48px)', fontWeight: 900, lineHeight: 1.1, marginBottom: 12, letterSpacing: '-1px', fontFamily: "'Syne', sans-serif", textShadow: '0 2px 20px rgba(0,0,0,.5)' }}>
                {featuredVideo.title}
              </h2>
              <p style={{ color: 'rgba(255,255,255,.65)', fontSize: 'clamp(12px, 1.8vw, 14px)', lineHeight: 1.7, marginBottom: 18, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {featuredVideo.description || 'No description available'}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
                <span style={{ color: 'rgba(255,255,255,.5)', fontSize: 13 }}>⏱ {fmtDuration(featuredVideo.duration)}</span>
                {featuredVideo.release_year > 0 && <span style={{ color: 'rgba(255,255,255,.5)', fontSize: 13 }}>📅 {featuredVideo.release_year}</span>}
                <span style={{ color: 'rgba(255,255,255,.5)', fontSize: 13 }}>👁 {fmtCount(featuredVideo.views)}</span>
                <span style={{ color: 'rgba(255,255,255,.5)', fontSize: 13 }}>❤️ {fmtCount(featuredVideo.likes_count)}</span>
              </div>
              <div className="hero-actions" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to={`/video/${featuredVideo.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '12px 28px', borderRadius: 12, background: 'linear-gradient(135deg, #e53935, #8e24aa)', color: '#fff', fontSize: 15, fontWeight: 700, textDecoration: 'none', boxShadow: '0 4px 24px rgba(229,57,53,.45)', whiteSpace: 'nowrap' }}>
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>Watch Now
                </Link>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 20px', borderRadius: 12, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)', color: '#fff', fontSize: 13, whiteSpace: 'nowrap' }}>
                  👤 {featuredVideo.user?.username}
                </div>
              </div>
            </div>
          </div>

          <div className="hero-thumbnails" style={{ position: 'absolute', right: 48, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 8, zIndex: 5 }}>
            {featured.map((v, i) => (
              <button key={v.id} onClick={() => setFeaturedIndex(i)} style={{ width: i === featuredIndex ? 56 : 44, height: i === featuredIndex ? 56 : 44, borderRadius: 10, overflow: 'hidden', border: i === featuredIndex ? '2px solid #e53935' : '2px solid rgba(255,255,255,.15)', background: '#111', cursor: 'pointer', transition: 'all .3s ease', flexShrink: 0, boxShadow: i === featuredIndex ? '0 0 16px rgba(229,57,53,.5)' : 'none', padding: 0 }}>
                <img src={v.thumbnail_url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </button>
            ))}
          </div>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80, background: `linear-gradient(to bottom, transparent, ${isDark ? '#080810' : '#f0f2f8'})` }} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          VIDEO GRID
      ═══════════════════════════════════════════════════════ */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(20px, 3vw, 32px) clamp(12px, 2vw, 48px) clamp(40px, 6vw, 64px)' }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'clamp(40px, 8vw, 80px) 0' }}>
            <div style={{ fontSize: 'clamp(40px, 8vw, 64px)', marginBottom: 16, filter: 'grayscale(1)', opacity: .4 }}>🎬</div>
            <h3 style={{ color: isDark ? '#fff' : '#0f172a', fontSize: 'clamp(16px, 3vw, 20px)', fontWeight: 700, marginBottom: 8 }}>No videos found</h3>
            <p style={{ color: isDark ? '#555' : '#94a3b8', fontSize: 'clamp(12px, 2vw, 14px)' }}>
              {searchQuery ? `No results for "${searchQuery}"` : 'Check back later for new content.'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'clamp(16px, 3vw, 28px)' }}>
              <div style={{ height: 1, flex: 1, background: isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.06)' }} />
              <span style={{ color: isDark ? '#444' : '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                {selectedType ? TYPE_META[selectedType].label : 'All Content'} · {filtered.length} titles
              </span>
              <div style={{ height: 1, flex: 1, background: isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.06)' }} />
            </div>

            <div className="video-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(150px, 25vw, 280px), 1fr))', gap: 'clamp(10px, 2vw, 24px)' }}>
              {paginatedVideos.map((video, idx) => (
                <VideoCard key={video.id} video={video} isDark={isDark} isHovered={hoveredCard === video.id} onHover={setHoveredCard} index={idx} />
              ))}
            </div>

            {paginatedVideos.length < filtered.length && (
              <div ref={loadMoreRef} style={{ textAlign: 'center', padding: 'clamp(20px, 4vw, 40px) 0' }}>
                <div style={{ width: 32, height: 32, border: '2px solid #e53935', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .8s linear infinite', margin: '0 auto' }} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}